import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keeps Tab / Shift+Tab inside `containerRef` while `active` is true. Restores
 * focus to whatever was focused before the trap activated when it tears down.
 * Modals + popovers should always use this — without it, users can Tab out of
 * an "open" overlay into the page underneath, which is both confusing and an
 * accessibility violation.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focus = (el: Element | null | undefined) => {
      if (el instanceof HTMLElement) el.focus();
    };

    const focusables = (): HTMLElement[] => {
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
    };

    // Focus the first interactive element on mount if focus isn't already
    // inside. If the container has none, focus the container itself (needs
    // tabIndex={-1} on the element for that to work).
    if (!container.contains(document.activeElement)) {
      const f = focusables();
      focus(f[0] ?? container);
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) {
        e.preventDefault();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && current === first) {
        e.preventDefault();
        focus(last);
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        focus(first);
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      // Restore prior focus only if the page didn't move it elsewhere already.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        focus(previouslyFocused);
      }
    };
  }, [active, containerRef]);
}
